import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { libraryQuestionPath } from '@/constants/appRoutes';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import {
  libraryQuestionsApi,
  type LibraryQuestion,
  type LibraryQuestionChannel,
} from '@/services/api';

const FILTERS: { id: LibraryQuestionChannel | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'staff', label: 'Staff' },
  { id: 'community', label: 'Community' },
];

function parseChannel(value: string | null): LibraryQuestionChannel | undefined {
  if (value === 'staff' || value === 'community') return value;
  return undefined;
}

function formatDate(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString();
}

function excerpt(body: string): string {
  const trimmed = body.trim();
  return trimmed.length > 220 ? `${trimmed.slice(0, 220).trim()}…` : trimmed;
}

interface LibraryQuestionListProps {
  query?: string;
}

export function LibraryQuestionList({ query }: LibraryQuestionListProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const channel = parseChannel(searchParams.get('channel'));
  const [items, setItems] = useState<LibraryQuestion[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    libraryQuestionsApi
      .list({ channel, q: query?.trim() || undefined, limit: 50 })
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        setTotal(data.total);
        setError(null);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load questions.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [channel, query]);

  const setChannel = (next: LibraryQuestionChannel | 'all') => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('channel');
    else params.set('channel', next);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="flex-1 overflow-y-auto container-scroll">
      <div className="mx-auto max-w-3xl px-5 py-5 md:px-8 md:py-7">
        <h1 className="font-display mb-2 text-3xl font-semibold tracking-tight text-foreground">
          {query?.trim() ? 'Search questions' : 'Questions and answers'}
        </h1>
        <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
          {query?.trim()
            ? `Results for “${query.trim()}”.`
            : 'Questions sent to the RCM team or the community, and the answers that come back.'}
        </p>

        <div className="mb-5 flex rounded-lg bg-muted/50 p-0.5" role="group" aria-label="Filter questions">
          {FILTERS.map(({ id, label }) => {
            const active = (channel ?? 'all') === id;
            return (
              <button
                key={id}
                type="button"
                aria-pressed={active}
                className={cn(
                  'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  active
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                onClick={() => setChannel(id)}
              >
                {label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner className="h-6 w-6" />
          </div>
        ) : error ? (
          <p className="py-10 text-center text-sm text-destructive">{error}</p>
        ) : items.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            {query?.trim() ? 'No questions match that search.' : 'No questions yet.'}
          </p>
        ) : (
          <ul className="space-y-3">
            {items.map((question) => (
              <li key={question.id}>
                <Link
                  to={libraryQuestionPath(question.id)}
                  className="block rounded-xl border border-border bg-card px-4 py-3 transition-colors hover:border-[hsl(var(--accent-brand)/0.4)] hover:bg-[hsl(var(--accent-brand)/0.04)]"
                >
                  <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="rounded-full bg-muted px-2 py-0.5 font-medium capitalize text-foreground">
                      {question.channel}
                    </span>
                    <span className="capitalize">{question.status}</span>
                    {question.author_name && <span>{question.author_name}</span>}
                    <span>{formatDate(question.created_at)}</span>
                    <span>
                      {question.answer_count} {question.answer_count === 1 ? 'answer' : 'answers'}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed text-foreground">{excerpt(question.body)}</p>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {!loading && !error && total > items.length && (
          <p className="mt-4 text-center text-xs text-muted-foreground">
            Showing {items.length} of {total}
          </p>
        )}
      </div>
    </div>
  );
}
